import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { UtensilsCrossed, ArrowRight, Package2, Clock, Shield, ChevronLeft, ChevronRight, Filter, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DiscountBadge } from '@/components/ui/discount-badge';
import { RecommendedProductCard } from '@/components/ui/recommended-product-card';
import { useStoreInfo } from '@/lib/store';
import { truncateText, formatPrice, getDomain } from '@/lib/utils';
import { calculateProductDiscount } from '@/lib/discount';
import { Product } from '@/types';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

interface Category {
  category: string;
  product_id: string;
  images: string[];
}

export function HomePage() {
  const navigate = useNavigate();
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [displayedProducts, setDisplayedProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const { storeInfo } = useStoreInfo();
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [sortBy, setSortBy] = useState<'price-asc' | 'price-desc' | 'name'>('name');
  const [searchTerm, setSearchTerm] = useState('');
  const [recommendedProduct, setRecommendedProduct] = useState<Product | null>(null);
  const [showRecommended, setShowRecommended] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const domain = getDomain();
        
        // Fetch categories
        const categoriesResponse = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/categories.php?domain=${domain}`);
        
        if (!categoriesResponse.headers.get('content-type')?.includes('application/json')) {
          throw new Error('Categories API returned non-JSON response');
        }
        
        const categoriesData = await categoriesResponse.json();
        
        if (categoriesData.status) {
          const uniqueCategories = categoriesData.products
            .filter((cat: Category) => cat.category && cat.category !== 'demo' && cat.category.trim() !== '')
            .reduce((acc: Category[], current: Category) => {
              const exists = acc.find(item => item.category === current.category);
              if (!exists) {
                acc.push(current);
              }
              return acc;
            }, []);
          setCategories(uniqueCategories);
        }

        // Fetch products
        const productsResponse = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/products.php?domain=${domain}`);
        
        if (!productsResponse.headers.get('content-type')?.includes('application/json')) {
          throw new Error('Products API returned non-JSON response');
        }
        
        const productsData = await productsResponse.json();

        if (productsData.status && productsData.products) {
          const activeProducts = productsData.products.filter((p: Product) => 
            p.status === 1 && p.totalStock > 0
          );
          setAllProducts(activeProducts);
          setDisplayedProducts(activeProducts.slice(0, 50));
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    // Show recommended product randomly (30% chance every 10 seconds)
    const showRecommendation = () => {
      if (Math.random() < 0.3 && allProducts.length > 0 && !showRecommended) {
        const randomProduct = allProducts[Math.floor(Math.random() * allProducts.length)];
        setRecommendedProduct(randomProduct);
        setShowRecommended(true);
      }
    };

    const interval = setInterval(showRecommendation, 10000);
    
    // Show initial recommendation after 5 seconds
    const initialTimeout = setTimeout(showRecommendation, 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(initialTimeout);
    };
  }, [allProducts, showRecommended]);

  useEffect(() => {
    let filtered = [...allProducts];

    if (selectedCategory) {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }

    if (searchTerm) {
      filtered = filtered.filter(p => 
        p.produt_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    switch (sortBy) {
      case 'price-asc':
        filtered.sort((a, b) => parseInt(a.sellingprice) - parseInt(b.sellingprice));
        break;
      case 'price-desc':
        filtered.sort((a, b) => parseInt(b.sellingprice) - parseInt(a.sellingprice));
        break;
      case 'name':
        filtered.sort((a, b) => a.produt_name.localeCompare(b.produt_name));
        break;
    }

    setDisplayedProducts(filtered.slice(0, 50));
  }, [allProducts, selectedCategory, sortBy, searchTerm]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(`/products?search=${encodeURIComponent(searchTerm)}`);
  };

  const features = [
    {
      icon: Package2,
      title: 'Quality Products',
      description: 'We ensure the highest quality for all our products'
    },
    {
      icon: Clock,
      title: 'Fast Delivery',
      description: 'Quick and reliable delivery to your doorstep'
    },
    {
      icon: Shield,
      title: 'Secure Payments',
      description: 'Multiple secure payment options available'
    }
  ];

  return (
    <>
      <Helmet>
        <title>{storeInfo?.site_name || 'Welcome'}</title>
        <meta name="description" content={storeInfo?.slogan || 'Welcome to our store'} />
      </Helmet>

      {/* Hero Section with Slider - Optimized for All Screens */}
      <div className="relative bg-brand-primary w-full">
        <Swiper
          modules={[Pagination, Autoplay]}
          pagination={{ clickable: true }}
          autoplay={{ delay: 5000 }}
          className="h-[200px] sm:h-[280px] md:h-[350px] lg:h-[450px] xl:h-[500px] w-full"
        >
          {storeInfo?.heroSlider?.length ? (
            storeInfo.heroSlider.map((slide, index) => (
              <SwiperSlide key={index}>
                <Link 
                  to="/categories"
                  className="block relative h-full w-full cursor-pointer"
                >
                  <div className="absolute inset-0 flex items-center justify-center">
                    <img
                      src={`https://marivieint.com/${slide}`}
                      alt={`Slide ${index + 1}`}
                      className="w-full h-full object-contain md:object-cover"
                    />
                  </div>
                  <div className="absolute inset-0 bg-black/30 hover:bg-black/40 transition-colors" />
                </Link>
              </SwiperSlide>
            ))
          ) : (
            <SwiperSlide>
              <div className="h-full w-full bg-gradient-to-r from-brand-dark to-brand-primary" />
            </SwiperSlide>
          )}
        </Swiper>
      </div>

      {/* Categories Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Shop by Category
            </h2>
            <Link to="/categories">
              <Button variant="outline" className="group">
                All Categories
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>

          <Swiper
            modules={[Navigation, Pagination]}
            spaceBetween={16}
            slidesPerView={2}
            navigation
            pagination={{ clickable: true }}
            breakpoints={{
              640: { slidesPerView: 3 },
              768: { slidesPerView: 4 },
              1024: { slidesPerView: 5 },
            }}
            className="category-swiper"
          >
            {categories.map((category) => (
              <SwiperSlide key={category.category}>
                <Link
                  to={`/products?category=${encodeURIComponent(category.category)}`}
                  className="block aspect-[4/3] relative overflow-hidden rounded-xl group"
                >
                  <img
                    src={category.images[0]}
                    alt={category.category}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-black/0 group-hover:from-black/70 transition-colors" />
                  <div className="absolute inset-0 flex items-center justify-center p-4">
                    <h3 className="text-lg font-bold text-white text-center transform transition-transform duration-300 group-hover:scale-105">
                      {category.category}
                    </h3>
                  </div>
                </Link>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </section>

    

      {/* Products Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col gap-4 mb-8">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Featured Products
              </h2>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="md:hidden p-2 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
              >
                <Filter className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSearch} className="relative">
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 h-12 rounded-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-brand-primary text-white">
                <ArrowRight className="h-4 w-4" />
              </button>
            </form>

            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Category
                      </label>
                      <select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="w-full h-10 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-600"
                      >
                        <option value="">All Categories</option>
                        {categories.map((cat) => (
                          <option key={cat.category} value={cat.category}>
                            {cat.category}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Sort By
                      </label>
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                        className="w-full h-10 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-600"
                      >
                        <option value="name">Name</option>
                        <option value="price-asc">Price: Low to High</option>
                        <option value="price-desc">Price: High to Low</option>
                      </select>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="hidden md:flex gap-4">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 h-10 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700"
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat.category} value={cat.category}>
                    {cat.category}
                  </option>
                ))}
              </select>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                className="px-4 h-10 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700"
              >
                <option value="name">Sort by Name</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {displayedProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={`/product/${product.id}`}
                  className="group block bg-white dark:bg-gray-800 rounded-2xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-300"
                >
                  <div className="aspect-square relative overflow-hidden">
                    <img
                      src={product.images[0]}
                      alt={product.produt_name}
                      className="w-full h-full object-cover transform transition-transform duration-300 group-hover:scale-105"
                    />
                    {product.postType === "1" && (
                      <div className="absolute top-2 right-2 bg-brand-primary text-white px-3 py-1 rounded-full text-sm font-medium">
                        Pre-Order
                      </div>
                    )}
                    {(() => {
                      const discountInfo = calculateProductDiscount(product, 1);
                      return discountInfo.hasDiscount && (
                        <div className="absolute top-2 left-2">
                          <DiscountBadge 
                            discountType={discountInfo.discountType}
                            discountAmount={discountInfo.discountAmount}
                          />
                        </div>
                      );
                    })()}
                  </div>
                  <div className="p-4">
                    <h3 className="font-medium text-gray-900 dark:text-white mb-2 group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors line-clamp-2">
                      {truncateText(product.produt_name, 50)}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                      {product.category || 'Uncategorized'}
                    </p>
                    <div className="flex justify-between items-center">
                      <div className="flex flex-col">
                        <span className="text-lg font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-200 bg-clip-text text-transparent">
                          {formatPrice(product.sellingprice)}
                        </span>
                        {(() => {
                          const discountInfo = calculateProductDiscount(product, 1);
                          return discountInfo.hasDiscount && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              Buy {discountInfo.threshold}+ for discount
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          {allProducts.length > 50 && (
            <div className="text-center mt-8">
              <Link to="/products">
                <Button variant="outline" size="lg" className="group">
                  View All Products
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Recommended Product Popup */}
      {showRecommended && recommendedProduct && (
        <RecommendedProductCard
          product={recommendedProduct}
          onClose={() => {
            setShowRecommended(false);
            setRecommendedProduct(null);
          }}
        />
      )}

        {/* Features Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center p-3 bg-brand-50 dark:bg-brand-primary/20 rounded-full mb-4">
                  <feature.icon className="h-6 w-6 text-brand-primary dark:text-brand-light" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}